import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  CircularProgress,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';

type ConfirmationMethod = 'phone' | 'email' | 'in_person';

interface ConfirmationMethodDialogProps {
  open: boolean;
  onConfirm: (method: ConfirmationMethod) => void;
  onCancel: () => void;
  loading?: boolean;
}

const methods: { value: ConfirmationMethod; icon: typeof PhoneIcon; labelKey: string }[] = [
  { value: 'phone', icon: PhoneIcon, labelKey: 'confirmationMethod.phone' },
  { value: 'email', icon: EmailIcon, labelKey: 'confirmationMethod.email' },
  { value: 'in_person', icon: StorefrontIcon, labelKey: 'confirmationMethod.inPerson' },
];

export function ConfirmationMethodDialog({ open, onConfirm, onCancel, loading }: ConfirmationMethodDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const [selected, setSelected] = useState<ConfirmationMethod>('phone');

  return (
    <ResponsiveDialog
      open={open}
      onClose={onCancel}
      mobileTitle={t('confirmationMethod.title')}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('confirmationMethod.title')}</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <List disablePadding>
          {methods.map(({ value, icon: Icon, labelKey }) => (
            <ListItemButton
              key={value}
              selected={selected === value}
              onClick={() => setSelected(value)}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Radio
                  checked={selected === value}
                  tabIndex={-1}
                  size="small"
                />
              </ListItemIcon>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t(labelKey)} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(selected)}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {tc('confirm')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
